import { ProgressBar } from '@pixi/ui';
import { getLocalizedValue } from '@softgames/localization';
import type { TaskViewConfig } from './TaskView';
import { TaskView } from './TaskView';
import type { ProgressTaskViewLayout } from './types';
import type { LevelTaskProgress } from '../types';
import type { LayoutText } from '../../../libs/layouts/populateLayouts/LayoutText';

export interface ProgressTaskViewConfig extends TaskViewConfig {
  layout: ProgressTaskViewLayout;
}

export class ProgressTaskView extends TaskView {
  public readonly progressBar: ProgressBar = new ProgressBar({
    bg: 'progressBarBG',
    fill: 'progressBar',
    fillPaddings: {
      top: 15.5,
      left: 8,
      right: 8,
    },
    nineSliceSprite: {
      bg: [20, 35, 50, 30],
      fill: [20, 20, 50, 20],
    },
    progress: 0,
  });

  private readonly initialFontSize: number;

  constructor(config: ProgressTaskViewConfig) {
    super(config);

    const { taskLayout, progressBar } = this;
    const { progressBarSlot } = taskLayout;

    this.initialFontSize = taskLayout.countText.style.fontSize;

    progressBarSlot.addChild(progressBar);
    this.updateProgressBar(false);

    this.progressBarWidth = 250;
  }

  public get taskLayout(): ProgressTaskViewLayout {
    return super.taskLayout as ProgressTaskViewLayout;
  }

  protected updateCountText(): void {
    const { progress } = this;
    const count = getLocalizedValue(progress.collected);
    const goal = getLocalizedValue(progress.goal);
    this.taskLayout.countText.text = `${count}/${goal}`;
  }

  public incrementProgress(amount: number): LevelTaskProgress {
    super.incrementProgress(amount);
    const { progress } = this;
    this.updateProgressBar(true);
    return progress;
  }

  private updateProgressBar(animate: boolean): void {
    const { progress, progressBar } = this;

    // fixedProgress accounts for overlap of the hex icon on the bar.
    const fixedProgress = (19 / progressBar.width) * 100;
    const progressRatio = fixedProgress + Math.round((progress.collected / progress.goal) * (100 - fixedProgress));

    if (progressBar.progress !== progressRatio) {
      progressBar.removeTweens();

      if (animate) progressBar.tweenTo({ progress: progressRatio }, 200, 'quadOut', true);
      else progressBar.progress = progressRatio;
    }
  }

  public set progressBarWidth(value: number) {
    const { progressBarSlot, countText, icon } = this.taskLayout;
    this.progressBar.width = value;
    countText.x = progressBarSlot.x + progressBarSlot.width / 2 + 10;
    (countText as LayoutText).setTextSizeRestrictions({
      maxWidth: value - icon.width / 2 - 20,
      maxFontSize: this.initialFontSize,
    });
  }

  public get progressBarWidth(): number {
    const { progressBarSlot } = this.taskLayout;
    return progressBarSlot.width;
  }
}
